// MedicalPrescriptionInterface.tsx - Version corrigée
import React, { useState } from "react";
import { Check } from "lucide-react";

// Import des composants
import Modal from "../ui/Modal";
import PrescriptionHeader from "../header/PrescriptionHeader";
import MedicationForm from "../form/MedicationForm";
import MedicationList from "../lists/MedicationList";
import PrescriptionPreview from "../preview/PrescriptionPreview";
import Notification from "../ui/Notification";

// Import des hooks et utilitaires
import { useEndDateCalculation } from "../../hooks/useEndDateCalculation";
import type { Medication } from "../../types/medication";
import { DEFAULT_MEDICATION } from "../../constants/medication";
import { generateOrderNumber } from "../../utils/medication";

interface MedicalPrescriptionInterfaceProps {
  onClose: () => void;
  onMedicationsChange: (meds: Medication[]) => void;
  medications: Medication[];
  onAddOrEditMedication?: (medication: Medication) => void;
}

const MedicalPrescriptionInterface: React.FC<
  MedicalPrescriptionInterfaceProps
> = ({ onClose, onMedicationsChange, medications }) => {
  const [showPrescriptionModal] = useState<boolean>(true);
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [showSaveConfirmation, setShowSaveConfirmation] =
    useState<boolean>(false);

  // États locaux pour le formulaire
  const [currentMedication, setCurrentMedication] = useState<Medication>({
    id: 0,
    orderNumber: "",
    ...DEFAULT_MEDICATION,
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [isEditing, setIsEditing] = useState<number | null>(null);

  // Hook pour calculer automatiquement la date de fin
  useEndDateCalculation(currentMedication, updateCurrentMedication);

  // Fonctions utilitaires
  function updateCurrentMedication(
    field: keyof Medication,
    value: string | number
  ) {
    setCurrentMedication((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function resetCurrentMedication() {
    setCurrentMedication({
      id: 0,
      orderNumber: "",
      ...DEFAULT_MEDICATION,
      startDate: new Date().toISOString().split("T")[0],
      endDate: new Date().toISOString().split("T")[0],
    });
  }

  // Gestion des médicaments
  const handleAddMedication = () => {
    if (currentMedication.name.trim()) {
      const newMedication: Medication = {
        ...currentMedication,
        id: Date.now(),
        orderNumber: generateOrderNumber(),
      };

      // Mettre à jour l'état parent
      const updatedMedications = [...medications, newMedication];
      onMedicationsChange(updatedMedications);

      // Réinitialiser le formulaire
      resetCurrentMedication();

      // Afficher la confirmation
      setShowSaveConfirmation(true);
      setTimeout(() => setShowSaveConfirmation(false), 3000);

      return true;
    }
    return false;
  };

  const handleUpdateMedication = (): void => {
    if (isEditing !== null) {
      const updatedMedications = medications.map((med) =>
        med.id === isEditing ? { ...currentMedication, id: isEditing } : med
      );
      onMedicationsChange(updatedMedications);
      setIsEditing(null);
      resetCurrentMedication();
    }
  };

  const handleDeleteMedication = (id: number) => {
    const updatedMedications = medications.filter((med) => med.id !== id);
    onMedicationsChange(updatedMedications);
  };

  const handleEditMedication = (medication: Medication) => {
    setCurrentMedication(medication);
    setIsEditing(medication.id);
  };

  const handleDuplicateMedication = (medication: Medication) => {
    const duplicated = {
      ...medication,
      id: Date.now(),
      orderNumber: generateOrderNumber(),
      name: `${medication.name} (Copie)`,
    };
    const updatedMedications = [...medications, duplicated];
    onMedicationsChange(updatedMedications);
  };

  const handleCancelEdit = () => {
    setIsEditing(null);
    resetCurrentMedication();
  };

  const handleSubmit = (): void => {
    
    onClose();
  };

  const handlePrint = (): void => {
    window.print();
  };

  const handleTogglePreview = (): void => {
    setIsPreviewMode(!isPreviewMode);
  };

  return (
    <div>
      <Modal isOpen={showPrescriptionModal} onClose={onClose}>
        <div className="flex flex-col h-full max-h-[95vh]">
          {/* Header */}
          <PrescriptionHeader
            medicationCount={medications.length}
            isPreviewMode={isPreviewMode}
            onTogglePreview={handleTogglePreview}
            onPrint={handlePrint}
            onClose={onClose}
          />

          {/* Notification de sauvegarde */}
          <Notification
            show={showSaveConfirmation}
            message="Médicament ajouté avec succès!"
            type="success"
          />

          <div className="flex-1 overflow-y-auto">
            {isPreviewMode ? (
              <PrescriptionPreview medications={medications} />
            ) : (
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
                  {/* Formulaire */}
                  <MedicationForm
                    currentMedication={currentMedication}
                    isEditing={Boolean(isEditing)}
                    onMedicationChange={updateCurrentMedication}
                    onSubmit={
                      isEditing ? handleUpdateMedication : handleAddMedication
                    }
                    onCancel={isEditing ? handleCancelEdit : undefined}
                  />

                  {/* Liste des médicaments */}
                  <MedicationList
                    medications={medications}
                    onEdit={handleEditMedication}
                    onDelete={handleDeleteMedication}
                    onDuplicate={handleDuplicateMedication}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              type="button"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={medications.length === 0}
              className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              <Check className="mr-2" size={18} />
              Valider l'ordonnance ({medications.length} médicament
              {medications.length !== 1 ? "s" : ""})
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MedicalPrescriptionInterface;
